import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";

/**
 * Global media player. Maintains a queue, knows how to play HTML5 audio
 * (mp3 / m4a / direct file URLs) and Spotify embeds. External links
 * (YouTube, generic web URLs that aren't audio or Spotify) are simply
 * opened in a new tab from the call site.
 */
const PlayerContext = createContext(null);

/* ---------- URL classification ---------- */
// Allow Spotify's locale prefix (e.g. /intl-en/track/...) and ignore query strings.
const SPOTIFY_RE = /^https?:\/\/(?:open\.)?spotify\.com\/(?:intl-[a-z]{2}\/)?(track|playlist|album|episode|show)\/([A-Za-z0-9]+)/i;
const AUDIO_EXT_RE = /\.(mp3|m4a|aac|ogg|wav|flac)(\?|$)/i;
const MIXCLOUD_RE = /^https?:\/\/(?:www\.)?mixcloud\.com\/([^/]+\/[^/?#]+)\/?/i;
const SOUNDCLOUD_RE = /^https?:\/\/(?:www\.)?soundcloud\.com\//i;
// Icecast/Shoutcast streams: explicit /stream or /listen paths, or common ports
const LIVE_STREAM_HINT_RE = /(\/stream|\/listen|:\d{4,5}\/?$|:\d{4,5}\/[a-z]+$|\.pls$|\.m3u8?$)/i;

export function classifyMedia(url) {
    if (!url) return "none";
    if (SPOTIFY_RE.test(url)) return "spotify";
    if (MIXCLOUD_RE.test(url)) return "mixcloud";
    if (SOUNDCLOUD_RE.test(url)) return "soundcloud";
    if (AUDIO_EXT_RE.test(url)) return "audio";
    if (LIVE_STREAM_HINT_RE.test(url)) return "audio"; // treat live streams as HTML5 audio
    return "external";
}

export function spotifyEmbedUrl(url) {
    const m = SPOTIFY_RE.exec(url);
    if (!m) return null;
    return `https://open.spotify.com/embed/${m[1]}/${m[2]}?utm_source=generator`;
}

export function mixcloudEmbedUrl(url) {
    const m = MIXCLOUD_RE.exec(url);
    if (!m) return null;
    return `https://www.mixcloud.com/widget/iframe/?feed=%2F${encodeURIComponent(m[1])}%2F&hide_cover=1&mini=1&light=0`;
}

export function soundcloudEmbedUrl(url) {
    return `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&color=%23E11D2A&inverse=true&auto_play=false&show_user=true`;
}

/* ---------- Provider ---------- */
export function MediaPlayerProvider({ children }) {
    const [queue, setQueue] = useState([]); // items: {id, title, host, image, url, kind}
    const [index, setIndex] = useState(0);
    const [playing, setPlaying] = useState(false);
    const [progress, setProgress] = useState(0); // 0..1 for audio
    const audioRef = useRef(null);

    const current = queue[index] || null;

    const playItem = useCallback((item, list) => {
        const kind = classifyMedia(item.url);
        const enriched = { ...item, kind };
        if (list && Array.isArray(list) && list.length) {
            const idx = list.findIndex((x) => x.id === item.id);
            setQueue(list.map((x) => ({ ...x, kind: classifyMedia(x.url) })));
            setIndex(idx >= 0 ? idx : 0);
        } else {
            setQueue([enriched]);
            setIndex(0);
        }
        setPlaying(true);
    }, []);

    const toggle = useCallback(() => setPlaying((p) => !p), []);
    const close = useCallback(() => {
        setPlaying(false);
        setQueue([]);
        setIndex(0);
        setProgress(0);
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.src = "";
        }
    }, []);

    const next = useCallback(() => {
        setIndex((i) => Math.min(i + 1, queue.length - 1));
        setPlaying(true);
    }, [queue.length]);

    const prev = useCallback(() => {
        setIndex((i) => Math.max(i - 1, 0));
        setPlaying(true);
    }, []);

    // Audio sync
    useEffect(() => {
        const a = audioRef.current;
        if (!a) return;
        if (!current || current.kind !== "audio") {
            a.pause();
            return;
        }
        if (a.src !== current.url) {
            a.src = current.url;
            a.load();
            setProgress(0);
        }
        if (playing) a.play().catch(() => setPlaying(false));
        else a.pause();
    }, [current, playing]);

    useEffect(() => {
        const a = audioRef.current;
        if (!a) return;
        const onTime = () => {
            if (a.duration) setProgress(a.currentTime / a.duration);
        };
        const onEnd = () => {
            if (index < queue.length - 1) next();
            else setPlaying(false);
        };
        a.addEventListener("timeupdate", onTime);
        a.addEventListener("ended", onEnd);
        return () => {
            a.removeEventListener("timeupdate", onTime);
            a.removeEventListener("ended", onEnd);
        };
    }, [index, queue.length, next]);

    const seek = useCallback((pct) => {
        const a = audioRef.current;
        if (!a || !a.duration) return;
        a.currentTime = Math.max(0, Math.min(1, pct)) * a.duration;
    }, []);

    const value = {
        queue, index, current, playing, progress,
        playItem, toggle, next, prev, close, seek,
    };

    return (
        <PlayerContext.Provider value={value}>
            {children}
            {/* Hidden audio element shared across the app */}
            <audio ref={audioRef} preload="metadata" />
        </PlayerContext.Provider>
    );
}

export function useMediaPlayer() {
    const ctx = useContext(PlayerContext);
    if (!ctx) throw new Error("useMediaPlayer must be used inside MediaPlayerProvider");
    return ctx;
}
